<template>
  <AppMessage
    v-for="message in filteredSystemMessages(messageFilters)"
    :key="message.id"
    :variant="message.notification_type"
    @close="dismissMessage(message)">
    <div v-html="DOMPurify.sanitize(getLocalizedContent(message))" />
  </AppMessage>
</template>

<script setup lang="ts">
import type {SystemMessage} from '@myparcel-frontend/types';
import {useProposition} from '@myparcel-frontend/proposition';
import DOMPurify from 'dompurify';
import {ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import AppMessage from '@/components/ui/AppMessages/AppMessage.vue';
import useSystemMessages from '@/composables/useSystemMessages';

const route = useRoute();
const {locale} = useI18n();

const {proposition} = useProposition();

const {additionalFilters, getSystemMessages, dismissMessage, filteredSystemMessages} = useSystemMessages();

const messageFilters = ref({});

const getLocalizedContent = (message: SystemMessage): string => {
  const lang = locale.value.slice(0, 2);
  const match = message.contents.find((content) => content.language === lang);

  // Fallback to default proposition language when message is not available in current language.
  // @ts-expect-error - 'backoffice' can technically be disabled in proposition, but then we wouldn't reach this code.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const fallbackLanguage: string = proposition.value.applications.backoffice.internationalization.language;
  const fallbackMessage = message.contents.find((content) => content.language === fallbackLanguage.slice(0, 2));

  return match?.content ?? fallbackMessage?.content ?? '';
};

watch(
  () => [route.name, additionalFilters.value],
  async () => {
    const additionalSystemMessageType = route.meta?.systemMessageType;

    messageFilters.value = {
      platformId: proposition.value.proposition.id,
      global: true,
      ...(additionalFilters.value ? {filter: additionalFilters.value} : {}),
      ...(additionalSystemMessageType ? {messageType: additionalSystemMessageType} : {}),
    };
    await getSystemMessages();
  },
  {immediate: true},
);
</script>
